import React, { useState, useEffect, useRef } from 'react';
import { baseUrl } from '../../shared';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import EditIcon from '@mui/icons-material/Edit';
import TextField from '@mui/material/TextField';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

export default function TeardownPhotos({ jobId, canManage }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [editingCaption, setEditingCaption] = useState(null); // { id, caption }
  const fileInputRef = useRef();

  const loadPhotos = () => {
    setLoading(true);
    fetch(baseUrl + 'workshop/jobs/' + jobId + '/teardown/photos', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setPhotos(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { if (jobId) loadPhotos(); }, [jobId]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    files.forEach(f => formData.append('myFile', f));

    const xhr = new XMLHttpRequest();
    xhr.open('POST', baseUrl + 'workshop/jobs/' + jobId + '/teardown/photos');
    xhr.setRequestHeader('username', localStorage.getItem('username'));
    xhr.setRequestHeader('bToken', localStorage.getItem('bToken'));

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setUploadProgress(Math.round((e.loaded * 100) / e.total));
    };

    xhr.onload = () => {
      setUploading(false);
      setUploadProgress(0);
      fileInputRef.current.value = '';
      loadPhotos();
    };

    xhr.onerror = () => {
      setUploading(false);
      setUploadProgress(0);
    };

    xhr.send(formData);
  };

  const handleDelete = async (photo) => {
    if (!window.confirm('Remove this photo?')) return;
    await fetch(baseUrl + 'workshop/jobs/' + jobId + '/teardown/photos/' + photo.id, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    loadPhotos();
  };

  const handleSaveCaption = async () => {
    if (!editingCaption) return;
    await fetch(baseUrl + 'workshop/jobs/' + jobId + '/teardown/photos/' + editingCaption.id, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ caption: editingCaption.caption }),
    });
    setEditingCaption(null);
    loadPhotos();
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
          Photos ({photos.length})
        </Typography>
        {canManage && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddPhotoAlternateIcon />}
              onClick={() => fileInputRef.current.click()}
              disabled={uploading}
            >
              Add Photos
            </Button>
          </>
        )}
      </Box>

      {uploading && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" color="text.secondary">Uploading... {uploadProgress}%</Typography>
        </Box>
      )}

      {loading && <CircularProgress size={24} />}

      {!loading && photos.length === 0 && (
        <Typography variant="body2" color="text.secondary">No photos uploaded yet.</Typography>
      )}

      {!loading && photos.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {photos.map(photo => (
            <Box key={photo.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 130 }}>
              <Box
                sx={{
                  position: 'relative',
                  width: 130, height: 110,
                  borderRadius: 1, overflow: 'hidden',
                  border: '1px solid', borderColor: 'divider',
                  '&:hover .photo-actions': { opacity: 1 },
                }}
              >
                <img
                  src={baseUrl + photo.proxyUrl}
                  alt={photo.image_name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <Box
                  className="photo-actions"
                  sx={{
                    position: 'absolute', inset: 0,
                    bgcolor: 'rgba(0,0,0,0.45)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                    opacity: 0, transition: 'opacity 0.15s',
                  }}
                >
                  <Tooltip title="View full size">
                    <IconButton size="small" sx={{ color: '#fff' }} onClick={() => setLightbox(photo)}>
                      <OpenInFullIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canManage && (
                    <Tooltip title="Edit caption">
                      <IconButton size="small" sx={{ color: '#fff' }} onClick={() => setEditingCaption({ id: photo.id, caption: photo.caption || '' })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canManage && (
                    <Tooltip title="Delete">
                      <IconButton size="small" sx={{ color: '#fff' }} onClick={() => handleDelete(photo)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              {photo.caption && (
                <Typography variant="caption" sx={{ mt: 0.25, fontSize: 10, color: 'text.secondary', textAlign: 'center', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {photo.caption}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightbox} onClose={() => setLightbox(null)} maxWidth="lg">
        {lightbox && (
          <Box sx={{ p: 1 }}>
            <img
              src={baseUrl + lightbox.proxyUrl}
              alt={lightbox.image_name}
              style={{ maxWidth: '100%', maxHeight: '80vh', display: 'block' }}
            />
            {lightbox.caption && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, px: 1 }}>
                {lightbox.caption}
              </Typography>
            )}
          </Box>
        )}
      </Dialog>

      {/* Caption edit dialog */}
      <Dialog open={!!editingCaption} onClose={() => setEditingCaption(null)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Photo Caption</Typography>
          <TextField
            size="small" fullWidth autoFocus
            label="Caption (appears on teardown report)"
            value={editingCaption?.caption || ''}
            onChange={e => setEditingCaption(c => ({ ...c, caption: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && handleSaveCaption()}
            inputProps={{ maxLength: 120 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
            <Button size="small" onClick={() => setEditingCaption(null)}>Cancel</Button>
            <Button size="small" variant="contained" onClick={handleSaveCaption}>Save</Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
